import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { t } from "@chuzi/shared/config";
import type {
  RealmId,
  SceneChoice,
  SceneListItem,
  SceneMapEntry,
  SceneMapResponse,
  StoryListItem,
} from "@chuzi/shared/types";
import { api } from "../lib/api";
import { buildStoryMediaBundle, type StoryMediaBundle } from "../lib/media-preloader";
import {
  resolveAdvanceTarget,
  resolveAutoContinueTarget,
  resolveSceneChoices,
  resolveSceneTextLines,
  resolveViewerExitChoice,
  isViewerExitChoice,
  isTerminalScene,
  VIEWER_EXIT_CHOICE_ID,
  sceneShowsDefaultSlate,
  sortScenesForFlow,
} from "../lib/scene-viewer-data";
import { SoundCueEngine } from "../lib/sound-cue-engine";
import { ChoiceFlashArrow } from "./choice-flash-arrow";
import { SceneTransmissionStage } from "./scene-transmission-stage";
import {
  computeTotalDuration,
  computeEffectiveSceneDuration,
  computeChoiceOpacity,
  resolveChoiceTiming,
  formatMs,
  normalizeSceneFontSize,
  DEFAULT_SCENE_FONT_SIZE,
} from "./generator-timeline";
import { DEFAULT_SLATE_DURATION_MS } from "../lib/scene-viewer-data";

interface StoryViewerProps {
  story: StoryListItem;
  scenes: SceneListItem[];
  realm: RealmId;
  coverboxUrl?: string | null;
  onClose: () => void;
}

type ViewerPhase = "loading" | "preparing" | "playing" | "ended" | "error";

export function StoryViewer({
  story,
  scenes,
  realm,
  coverboxUrl,
  onClose,
}: StoryViewerProps) {
  const isWilds = realm === "wilds";
  const accent = isWilds ? "rgba(120,200,120,0.9)" : "rgba(126,184,255,0.9)";
  const borderColor = isWilds ? "rgba(90,180,90,0.35)" : "rgba(126,184,255,0.35)";

  const [phase, setPhase] = useState<ViewerPhase>("loading");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [preloadRatio, setPreloadRatio] = useState(0);
  const [viewerScenes, setViewerScenes] = useState<SceneListItem[]>(() => sortScenesForFlow(scenes));
  const [sceneMapData, setSceneMapData] = useState<SceneMapResponse | null>(null);
  const [mediaBundle, setMediaBundle] = useState<StoryMediaBundle | null>(null);
  const [currentSceneId, setCurrentSceneId] = useState<string | null>(null);
  const [playheadMs, setPlayheadMs] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [waitingForChoice, setWaitingForChoice] = useState(false);
  const [stageOpacity, setStageOpacity] = useState(1);
  const [visitedPath, setVisitedPath] = useState<string[]>([]);

  const soundEngineRef = useRef(new SoundCueEngine());
  const playbackAnimRef = useRef<number>(0);
  const playbackLastTimeRef = useRef<number>(0);
  const transitioningRef = useRef(false);
  const playStartTrackedRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const sceneById = useMemo(
    () => new Map(viewerScenes.map((s) => [s.id, s])),
    [viewerScenes],
  );

  const currentScene = currentSceneId ? sceneById.get(currentSceneId) ?? null : null;
  const currentEntry: SceneMapEntry | null =
    currentSceneId && sceneMapData ? sceneMapData.scene_map[currentSceneId] ?? null : null;

  const textLines = useMemo(
    () => (currentScene ? resolveSceneTextLines(currentScene) : []),
    [currentScene],
  );
  const baseChoices = useMemo(
    () => resolveSceneChoices(currentEntry),
    [currentEntry],
  );
  const choices = useMemo(() => {
    if (!currentScene) return baseChoices;
    const exitChoice = resolveViewerExitChoice(
      currentScene,
      viewerScenes,
      currentEntry,
      baseChoices,
    );
    if (!exitChoice) return baseChoices;
    return [...baseChoices, exitChoice];
  }, [baseChoices, currentScene, viewerScenes, currentEntry]);
  const textFontFamily = currentScene?.text_style?.font_family ?? "inherit";
  const textColor = currentScene?.text_style?.text_color ?? "#1a1a2e";
  const textBackgroundColor = currentScene?.text_style?.background_color ?? "#f5f0e8";
  const baseFontSize = normalizeSceneFontSize(currentScene?.text_style?.font_size ?? DEFAULT_SCENE_FONT_SIZE);
  const textWordCount = useMemo(() => {
    const allText = textLines.map((l) => ("text" in l ? (l as any).text : "")).join(" ");
    return allText.trim().split(/\s+/).filter(Boolean).length;
  }, [textLines]);
  const textFontSize = useMemo(() => {
    if (textWordCount <= 0) return baseFontSize;
    if (textWordCount <= 60) return Math.min(150, Math.round(baseFontSize * 1.5));
    if (textWordCount <= 100) return Math.min(150, Math.round(baseFontSize * 1.3));
    if (textWordCount <= 160) return Math.min(150, Math.round(baseFontSize * 1.15));
    return baseFontSize;
  }, [baseFontSize, textWordCount]);
  const sceneDurationSec = currentScene?.choice_end_time_seconds ?? null;
  const showDefaultSlate = currentScene ? sceneShowsDefaultSlate(currentScene) : false;

  const totalDurationMs = computeTotalDuration(textLines);
  const effectiveDurationMs = useMemo(() => {
    if (showDefaultSlate) return DEFAULT_SLATE_DURATION_MS;
    return computeEffectiveSceneDuration(totalDurationMs, choices, sceneDurationSec);
  }, [showDefaultSlate, totalDurationMs, choices, sceneDurationSec]);
  const fallbackAppearMs = Math.max(0, effectiveDurationMs - 3000);

  const mediaUrls = useMemo(() => {
    const map: Record<string, string> = {};
    if (!mediaBundle) return map;
    for (const [id, url] of mediaBundle.urlByMediaId) {
      map[id] = url;
    }
    return map;
  }, [mediaBundle]);

  const primaryMediaUrl =
    currentEntry?.media_url ??
    (currentScene?.media_id ? mediaUrls[currentScene.media_id] : null);

  /* ── bootstrap: fresh scenes + scene-map + preload ── */
  useEffect(() => {
    let cancelled = false;
    setPhase("loading");
    setLoadError(null);

    const scenesPromise = api.scenes.index(story.id);

    Promise.all([scenesPromise, api.watch.sceneMap(story.id)])
      .then(async ([freshScenes, map]) => {
        if (cancelled) return;
        const sorted = sortScenesForFlow(freshScenes);
        setViewerScenes(sorted);
        setSceneMapData(map);
        setPhase("preparing");
        const bundle = await buildStoryMediaBundle(sorted, map.scene_map, (ratio) => {
          if (!cancelled) setPreloadRatio(ratio);
        });
        if (cancelled) return;
        setMediaBundle(bundle);
        soundEngineRef.current.setBuffers(bundle.audioBuffers);
        const startId = map.start_scene_id || sorted.find((s) => s.is_title)?.id || sorted[0]?.id || null;
        setCurrentSceneId(startId);
        setVisitedPath(startId ? [startId] : []);
        setPlayheadMs(0);
        setPhase("playing");
        setIsPlaying(true);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(err instanceof Error ? err.message : "Failed to load story");
        setPhase("error");
      });

    return () => {
      cancelled = true;
    };
  }, [story.id]);

  useEffect(() => {
    if (phase === "playing" && !playStartTrackedRef.current) {
      playStartTrackedRef.current = true;
      api.watch.trackEngagement(story.id, { event: "play_start" }).catch(() => {});
    }
  }, [phase, story.id]);

  useEffect(() => {
    containerRef.current?.focus();
    return () => {
      soundEngineRef.current.dispose();
      cancelAnimationFrame(playbackAnimRef.current);
    };
  }, []);

  /* ── scene transition ── */
  const transitionToScene = useCallback(
    (targetSceneId: string) => {
      if (transitioningRef.current || !targetSceneId) return;
      transitioningRef.current = true;
      soundEngineRef.current.resetScene();
      setIsPlaying(false);
      setWaitingForChoice(false);
      setStageOpacity(0);

      window.setTimeout(() => {
        setCurrentSceneId(targetSceneId);
        setPlayheadMs(0);
        setVisitedPath((prev) => [...prev, targetSceneId]);
        setStageOpacity(1);
        setIsPlaying(true);
        transitioningRef.current = false;
      }, 180);
    },
    [],
  );

  const handleChoiceSelect = useCallback(
    (choice: SceneChoice) => {
      if (isViewerExitChoice(choice)) {
        setWaitingForChoice(false);
        setIsPlaying(false);
        api.watch
          .trackEngagement(story.id, { event: "choice_click", choice_id: choice.id })
          .catch(() => {});
        onClose();
        return;
      }
      if (!choice.target_scene_id) return;
      setWaitingForChoice(false);
      api.watch
        .trackEngagement(story.id, { event: "choice_click", choice_id: choice.id })
        .catch(() => {});
      transitionToScene(choice.target_scene_id);
    },
    [story.id, transitionToScene, onClose],
  );

  /* ── choice pause detection ── */
  useEffect(() => {
    if (choices.length === 0) {
      setWaitingForChoice(false);
      return;
    }
    const visibleChoices = choices.filter((choice) => {
      const timing = resolveChoiceTiming(choice, fallbackAppearMs);
      return computeChoiceOpacity(timing, playheadMs) > 0;
    });
    if (visibleChoices.length === 0) {
      setWaitingForChoice(false);
      return;
    }
    const shouldPause = visibleChoices.some((choice) => choice.pause_for_choice !== false);
    setWaitingForChoice(shouldPause && isPlaying);
  }, [playheadMs, isPlaying, choices, fallbackAppearMs]);

  /* ── playback loop ── */
  useEffect(() => {
    if (!isPlaying || waitingForChoice || phase !== "playing") return;

    playbackLastTimeRef.current = performance.now();
    const tick = () => {
      const now = performance.now();
      const delta = now - playbackLastTimeRef.current;
      playbackLastTimeRef.current = now;

      setPlayheadMs((prev) => {
        const next = prev + delta;
        if (next >= effectiveDurationMs) {
          return effectiveDurationMs;
        }
        return next;
      });

      playbackAnimRef.current = requestAnimationFrame(tick);
    };
    playbackAnimRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(playbackAnimRef.current);
  }, [isPlaying, waitingForChoice, phase, effectiveDurationMs]);

  /* ── sound cue sync ── */
  useEffect(() => {
    if (!mediaBundle || !currentScene) return;
    const soundLines = textLines.filter((l) => l.type === "sound" && l.media_id);
    soundEngineRef.current.sync(
      soundLines,
      playheadMs,
      isPlaying && !waitingForChoice,
      mediaBundle.urlByMediaId,
    );
  }, [playheadMs, isPlaying, waitingForChoice, textLines, mediaBundle, currentScene]);

  /* ── scene end / auto-advance ── */
  useEffect(() => {
    if (phase !== "playing" || !currentScene) return;
    if (playheadMs < effectiveDurationMs - 50) return;

    const terminalScene = isTerminalScene(currentScene, viewerScenes, currentEntry);

    const visibleChoices = choices.filter((c) => {
      const timing = resolveChoiceTiming(c, fallbackAppearMs);
      return computeChoiceOpacity(timing, playheadMs) > 0;
    });

    if (visibleChoices.length > 0) {
      if (!isPlaying) return;
      if (visibleChoices.some((c) => c.pause_for_choice !== false)) {
        setIsPlaying(false);
        if (
          terminalScene &&
          visibleChoices.some((c) => isViewerExitChoice(c)) &&
          !visibleChoices.some((c) => !isViewerExitChoice(c) && c.pause_for_choice !== false)
        ) {
          api.watch
            .trackEngagement(story.id, {
              event: "choice_click",
              choice_id: VIEWER_EXIT_CHOICE_ID,
            })
            .catch(() => {});
          onClose();
        }
      }
      return;
    }

    const autoContinueTarget = resolveAutoContinueTarget(choices);
    if (autoContinueTarget && autoContinueTarget !== currentScene.id) {
      transitionToScene(autoContinueTarget);
      return;
    }

    const advanceTarget = resolveAdvanceTarget(
      currentScene,
      viewerScenes,
      currentEntry,
    );

    if (advanceTarget && advanceTarget !== currentScene.id) {
      transitionToScene(advanceTarget);
      return;
    }

    if (playheadMs >= effectiveDurationMs - 50) {
      setIsPlaying(false);
      if (terminalScene) {
        api.watch
          .trackEngagement(story.id, {
            event: "choice_click",
            choice_id: VIEWER_EXIT_CHOICE_ID,
          })
          .catch(() => {});
        onClose();
      } else {
        setPhase("ended");
      }
    }
  }, [
    playheadMs,
    effectiveDurationMs,
    isPlaying,
    choices,
    currentScene,
    currentEntry,
    fallbackAppearMs,
    phase,
    transitionToScene,
    viewerScenes,
    story.id,
    onClose,
  ]);

  /* ── reset sound engine on scene change ── */
  useEffect(() => {
    soundEngineRef.current.resetScene();
    setPlayheadMs(0);
  }, [currentSceneId]);

  /* ── keyboard ── */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
      if (e.key === " " && phase === "playing") {
        e.preventDefault();
        setIsPlaying((p) => !p);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, phase]);

  const choiceOverlay: ReactNode = choices.map((choice) => {
    const timing = resolveChoiceTiming(choice, fallbackAppearMs);
    const opacity = computeChoiceOpacity(timing, playheadMs);
    if (opacity <= 0) return null;
    return (
      <button
        key={choice.id}
        type="button"
        onClick={() => handleChoiceSelect(choice)}
        title={choice.label ? t(realm, choice.label, choice.label) : undefined}
        style={{
          position: "absolute",
          left: `${choice.x}%`,
          top: `${choice.y}%`,
          width: `${choice.w}%`,
          height: `${choice.h}%`,
          cursor: "pointer",
          zIndex: 20,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "transparent",
          border: "none",
          padding: 0,
          opacity,
          transition: "opacity 0.05s linear",
        }}
      >
        <ChoiceFlashArrow
          color={accent}
          rotation={choice.arrow_rotation}
          scale={choice.arrow_scale}
        />
        {choice.label ? (
          <span
            style={{
              position: "absolute",
              bottom: -18,
              left: "50%",
              transform: "translateX(-50%)",
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "rgba(232,240,255,0.85)",
              textShadow: "0 1px 6px rgba(0,0,0,0.9)",
              whiteSpace: "nowrap",
              pointerEvents: "none",
            }}
          >
            {t(realm, choice.label, choice.label)}
          </span>
        ) : null}
      </button>
    );
  });

  const handleSeek = useCallback(
    (ms: number) => {
      const clamped = Math.max(0, Math.min(ms, effectiveDurationMs));
      setPlayheadMs(clamped);
      playbackLastTimeRef.current = performance.now();
    },
    [effectiveDurationMs],
  );

  const handleFilmEnded = useCallback(() => {
    if (choices.length > 0) {
      const autoContinueTarget = resolveAutoContinueTarget(choices);
      if (autoContinueTarget && autoContinueTarget !== currentScene?.id) {
        transitionToScene(autoContinueTarget);
        return;
      }
      setIsPlaying(false);
      return;
    }
    if (!currentScene) return;
    const advanceTarget = resolveAdvanceTarget(
      currentScene,
      viewerScenes,
      currentEntry,
    );
    if (advanceTarget && advanceTarget !== currentScene.id) {
      transitionToScene(advanceTarget);
    } else {
      api.watch
        .trackEngagement(story.id, {
          event: "choice_click",
          choice_id: VIEWER_EXIT_CHOICE_ID,
        })
        .catch(() => {});
      onClose();
    }
  }, [choices.length, currentScene, currentEntry, transitionToScene, viewerScenes, story.id, onClose]);

  const renderStage = () => {
    if (!currentScene) return null;
    return (
      <SceneTransmissionStage
        scene={currentScene}
        story={story}
        realm={realm}
        textLines={textLines}
        playheadMs={playheadMs}
        isPlaying={isPlaying && !waitingForChoice}
        fontFamily={textFontFamily}
        textColor={textColor}
        backgroundColor={textBackgroundColor}
        fontSize={textFontSize}
        primaryMediaUrl={primaryMediaUrl}
        mediaUrls={mediaUrls}
        coverboxUrl={coverboxUrl ?? null}
        effectiveDurationMs={effectiveDurationMs}
        overlay={choices.length > 0 ? choiceOverlay : undefined}
        readOnly
        fullScreen
        showTransport={false}
        headerLabel={t(realm, "viewer_stage_label", "Experience")}
        onFilmEnded={handleFilmEnded}
      />
    );
  };

  const progressPct = Math.min(
    100,
    (playheadMs / Math.max(effectiveDurationMs, 1)) * 100,
  );

  return (
    <div
      ref={containerRef}
      tabIndex={-1}
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 100,
        display: "flex",
        flexDirection: "column",
        background: phase === "playing" ? "#000" : "rgba(4, 7, 13, 0.88)",
        backdropFilter: phase === "playing" ? undefined : "blur(6px)",
        animation: "editorFadeIn 0.3s ease-out",
        overflow: "hidden",
      }}
    >
      <button
        onClick={onClose}
        style={{
          position: "absolute",
          top: 20,
          right: 20,
          background: "rgba(232,240,255,0.08)",
          border: `1px solid ${borderColor}`,
          borderRadius: 8,
          width: 36,
          height: 36,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          zIndex: 2,
        }}
        title={t(realm, "viewer_close", "Exit")}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M4 4l8 8M12 4l-8 8" stroke="rgba(232,240,255,0.7)" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>

      <style>{`
        .chuzi-viewer-scrub::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #e8f0ff;
          border: 2px solid ${isWilds ? "#78c878" : "#7eb8ff"};
          box-shadow: 0 0 10px ${isWilds ? "rgba(120,200,120,0.45)" : "rgba(126,184,255,0.45)"};
          cursor: grab;
        }
        .chuzi-viewer-scrub::-moz-range-thumb {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #e8f0ff;
          border: 2px solid ${isWilds ? "#78c878" : "#7eb8ff"};
          box-shadow: 0 0 10px ${isWilds ? "rgba(120,200,120,0.45)" : "rgba(126,184,255,0.45)"};
          cursor: grab;
        }
      `}</style>

      <div
        style={{
          flex: 1,
          width: "min(96vw, 1200px)",
          maxWidth: "100%",
          minHeight: 0,
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          padding: "56px 24px 20px",
          boxSizing: "border-box",
        }}
      >
        {(phase === "loading" || phase === "preparing") ? (
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 16,
            }}
          >
            <div style={{ fontSize: 13, color: "rgba(232,240,255,0.6)", letterSpacing: 0.6 }}>
              {phase === "loading"
                ? t(realm, "viewer_loading", "Loading experience…")
                : t(realm, "viewer_preparing", "Cueing media…")}
            </div>
            <div
              style={{
                width: 240,
                height: 4,
                borderRadius: 999,
                background: "rgba(232,240,255,0.08)",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${Math.round((phase === "loading" ? 0.15 : preloadRatio) * 100)}%`,
                  height: "100%",
                  background: accent,
                  transition: "width 0.2s ease",
                }}
              />
            </div>
          </div>
        ) : phase === "error" ? (
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 12,
            }}
          >
            <div style={{ fontSize: 14, color: "#ff9aa2", textAlign: "center", maxWidth: 420 }}>
              {loadError ?? t(realm, "viewer_loading", "Loading experience…")}
            </div>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: "8px 20px",
                borderRadius: 8,
                border: `1px solid ${borderColor}`,
                background: "rgba(232,240,255,0.06)",
                color: "#e8f0ff",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {t(realm, "viewer_close", "Exit")}
            </button>
          </div>
        ) : phase === "ended" ? (
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 12,
            }}
          >
            <div style={{ fontSize: 18, fontWeight: 700, color: "#e8f0ff" }}>
              {t(realm, "viewer_complete", "Experience complete")}
            </div>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: "8px 20px",
                borderRadius: 8,
                border: `1px solid ${borderColor}`,
                background: "rgba(232,240,255,0.06)",
                color: "#e8f0ff",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {t(realm, "viewer_close", "Exit")}
            </button>
          </div>
        ) : (
          <>
            <div
              style={{
                flex: 1,
                width: "100%",
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                opacity: stageOpacity,
                transition: "opacity 0.18s ease",
              }}
            >
              {renderStage()}
            </div>

            <div
              style={{
                width: "100%",
                flexShrink: 0,
                marginTop: 14,
                padding: "10px 14px",
                borderRadius: 10,
                border: `1px solid ${isWilds ? "rgba(120,200,120,0.2)" : "rgba(126,184,255,0.2)"}`,
                background: "rgba(6,10,18,0.92)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  marginBottom: 8,
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "rgba(232,240,255,0.75)",
                    letterSpacing: 0.6,
                    textTransform: "uppercase",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {story.title}
                  {currentScene ? (
                    <span style={{ color: accent, fontWeight: 700 }}>
                      {" · "}
                      {currentScene.title}
                    </span>
                  ) : null}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#e8f0ff",
                    fontVariantNumeric: "tabular-nums",
                    flexShrink: 0,
                  }}
                >
                  {formatMs(playheadMs)}
                  <span style={{ color: "rgba(232,240,255,0.4)", fontWeight: 500 }}>
                    {" / "}
                    {formatMs(effectiveDurationMs)}
                  </span>
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <button
                  type="button"
                  onClick={() => setIsPlaying((p) => !p)}
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 8,
                    border: `1px solid ${borderColor}`,
                    background: "rgba(232,240,255,0.08)",
                    color: "#e8f0ff",
                    cursor: "pointer",
                    fontSize: 14,
                    flexShrink: 0,
                  }}
                  title={
                    isPlaying && !waitingForChoice
                      ? t(realm, "editor_preview_pause", "Pause")
                      : t(realm, "editor_preview_play", "Play")
                  }
                >
                  {isPlaying && !waitingForChoice ? "⏸" : "▶"}
                </button>

                <div style={{ flex: 1, position: "relative", height: 28, display: "flex", alignItems: "center" }}>
                  <div
                    style={{
                      position: "absolute",
                      left: 0,
                      right: 0,
                      height: 6,
                      borderRadius: 999,
                      background: "rgba(232,240,255,0.1)",
                      overflow: "hidden",
                      pointerEvents: "none",
                    }}
                  >
                    <div
                      style={{
                        width: `${progressPct}%`,
                        height: "100%",
                        background: `linear-gradient(90deg, ${accent.replace("0.9", "0.55")}, ${accent})`,
                        boxShadow: `0 0 12px ${accent.replace("0.9", "0.35")}`,
                        transition: isPlaying ? "width 0.08s linear" : "width 0.15s ease",
                      }}
                    />
                  </div>
                  <input
                    className="chuzi-viewer-scrub"
                    type="range"
                    min={0}
                    max={Math.max(effectiveDurationMs, 1)}
                    step={50}
                    value={playheadMs}
                    onChange={(e) => handleSeek(Number(e.target.value))}
                    aria-label={t(realm, "viewer_scrub_label", "Playback position")}
                    style={{
                      position: "relative",
                      width: "100%",
                      height: 28,
                      margin: 0,
                      cursor: "pointer",
                      appearance: "none",
                      WebkitAppearance: "none",
                      background: "transparent",
                      accentColor: accent,
                    }}
                  />
                </div>

                {waitingForChoice ? (
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      color: accent,
                      flexShrink: 0,
                    }}
                  >
                    {t(realm, "viewer_choose_path", "Choose")}
                  </div>
                ) : null}
              </div>
            </div>

            {visitedPath.length > 1 ? (
              <div
                style={{
                  marginTop: 8,
                  fontSize: 9,
                  color: "rgba(232,240,255,0.35)",
                  letterSpacing: "0.06em",
                  textAlign: "center",
                }}
              >
                {t(realm, "watch_path", "Your path")}:{" "}
                {visitedPath.map((id) => sceneById.get(id)?.title ?? id).join(" → ")}
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
